import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  MousePointer,
  Calendar,
  MessageSquare,
  Eye,
  TrendingUp,
  LogOut,
  RefreshCw,
  Mail,
  Building,
  Clock,
  Activity,
  Zap,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, trend, trendValue, color, delay = 0 }) => (
  <div 
    className="relative bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 overflow-hidden group hover:border-white/20 transition-all duration-500"
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Glow effect */}
    <div 
      className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] opacity-20 group-hover:opacity-40 transition-opacity duration-500`}
      style={{ backgroundColor: color }}
    />
    
    <div className="relative">
      <div className="flex items-start justify-between mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color: color }} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm ${trend === 'up' ? 'text-[#b4dc19]' : 'text-red-400'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <p className="text-white/50 text-sm mb-1">{title}</p>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  </div>
);

// Chart Component (Simple Bar Chart)
const SimpleChart = ({ data, title }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#3dd3ee]" />
        {title}
      </h3>
      <div className="flex items-end gap-2 h-40">
        {data.length === 0 ? (
          <p className="text-white/50 text-sm">Aucune donnée disponible</p>
        ) : (
          data.slice(-14).map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center gap-2">
              <div 
                className="w-full bg-gradient-to-t from-[#3dd3ee] to-[#c8aff0] rounded-t-lg transition-all duration-500 hover:opacity-80"
                style={{ 
                  height: `${(item.count / maxValue) * 100}%`,
                  minHeight: item.count > 0 ? '8px' : '2px',
                }}
              />
              <span className="text-[10px] text-white/40 rotate-[-45deg] origin-top-left whitespace-nowrap">
                {item.date.slice(5)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Contact Card Component
const ContactCard = ({ contact }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-[#3dd3ee]/30 transition-all duration-300">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3dd3ee] to-[#c8aff0] flex items-center justify-center text-[#0a0a0b] font-bold text-sm">
            {contact.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-white font-medium">{contact.name}</p>
            <p className="text-white/50 text-sm flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {contact.email}
            </p>
          </div>
        </div>
        <span className="text-xs text-white/40 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDate(contact.created_at)}
        </span>
      </div>
      {contact.company && (
        <p className="text-white/60 text-sm flex items-center gap-1 mb-2">
          <Building className="w-3 h-3" />
          {contact.company}
        </p>
      )}
      <p className="text-white/70 text-sm line-clamp-2">{contact.message}</p>
      {contact.offer && (
        <span className="inline-block mt-2 px-2 py-1 bg-[#3dd3ee]/10 text-[#3dd3ee] text-xs rounded-lg">
          {contact.offer}
        </span>
      )}
    </div>
  );
};

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  if (isLoading && !stats) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#3dd3ee]/30 border-t-[#3dd3ee] rounded-full animate-spin" />
          <p className="text-white/50">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] relative overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-[#3dd3ee]/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[800px] h-[800px] bg-[#c8aff0]/5 rounded-full blur-[200px]" />
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(61, 211, 238, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(61, 211, 238, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative border-b border-white/10 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-white">Index</span>
                <span 
                  className="text-2xl font-bold bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #3dd3ee, #c8aff0)' }}
                >
                  Core
                </span>
              </div>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5 text-[#3dd3ee]" />
                <span className="text-white font-medium">Dashboard</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button
                onClick={fetchStats}
                disabled={isLoading}
                variant="outline"
                size="sm"
                className="border-white/20 text-white/70 hover:text-white hover:bg-white/10"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <span className="text-white/40 text-sm">
                Mis à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
              </span>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Activity className="w-8 h-8 text-[#3dd3ee]" />
            Tableau de bord
          </h1>
          <p className="text-white/50">Suivi en temps réel des performances du site IndexCore</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KPICard
            title="Demandes de contact"
            value={stats?.total_contacts || 0}
            icon={MessageSquare}
            color="#3dd3ee"
            delay={0}
          />
          <KPICard
            title="Clics Calendly"
            value={stats?.calendly_clicks || 0}
            icon={Calendar}
            color="#c8aff0"
            delay={100}
          />
          <KPICard
            title="Clics CTA"
            value={stats?.cta_clicks || 0}
            icon={MousePointer}
            color="#fad24b"
            delay={200}
          />
          <KPICard
            title="Vues projets"
            value={stats?.project_views || 0}
            icon={Eye}
            color="#b4dc19"
            delay={300}
          />
        </div>

        {/* Total Events Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-gradient-to-br from-[#3dd3ee]/20 to-[#c8aff0]/20 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3dd3ee] to-[#c8aff0] rounded-2xl flex items-center justify-center">
                <Zap className="w-8 h-8 text-[#0a0a0b]" />
              </div>
              <div>
                <p className="text-white/50 text-sm">Total événements trackés</p>
                <p className="text-4xl font-bold text-white">{stats?.total_events || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-2 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#b4dc19]" />
              Répartition par type
            </h3>
            <div className="flex flex-wrap gap-3">
              {stats?.events_by_type && Object.entries(stats.events_by_type).map(([type, count]) => (
                <div key={type} className="px-4 py-2 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-white/50 text-sm">{type}:</span>
                  <span className="text-white font-bold ml-2">{count}</span>
                </div>
              ))}
              {(!stats?.events_by_type || Object.keys(stats.events_by_type).length === 0) && (
                <p className="text-white/50 text-sm">Aucun événement enregistré</p>
              )}
            </div>
          </div>
        </div>

        {/* Chart and Recent Contacts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Events Chart */}
          <SimpleChart 
            data={stats?.events_by_day || []} 
            title="Événements (14 derniers jours)"
          />

          {/* Recent Contacts */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#3dd3ee]" />
              Dernières demandes de contact
            </h3>
            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {stats?.recent_contacts && stats.recent_contacts.length > 0 ? (
                stats.recent_contacts.map((contact, index) => (
                  <ContactCard key={contact.id || index} contact={contact} />
                ))
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/50">Aucune demande de contact</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(61, 211, 238, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(61, 211, 238, 0.5);
        }
      `}</style>
    </div>
  );
};

export default AdminDashboardPage;
